"use client"

import { createContext, useContext, useReducer, useEffect } from "react"

// Import des services
import {
  getClasseurs,
  createClasseur,
  updateClasseur,
  deleteClasseur,
  getClasseurID,
} from "../services/classeur.service"
import { getProjets, getProjetById } from "../services/projet.service"
import { getFeuilles, createFeuille, updateFeuille, deleteFeuille, getFeuilleID } from "../services/feuille.service"
import { getChampTypes, createChampType } from "../services/champType.service"
import { getFeuilleLignes, addFeuilleLigne, updateFeuilleLigne, updateFeuilleLigneRang, deleteFeuilleLigneColumns } from "../services/feuilleLigne.service"
import { getFeuilleData, createFeuilleData, updateFeuilleData, deleteFeuilleData, validateFeuilleData } from "../services/feuilleData.service"

const AppContext = createContext()

const initialState = {
  // Données principales
  projets: [],
  classeurs: [],
  feuilles: [],
  champTypes: [],
  lignes: [],

  // États de l'application
  loading: false,
  error: null,
  success: null,

  // Authentification
  user: null,
  token: null,

  // Cache pour optimiser les performances
  cache: {
    lastFetch: {},
    data: {},
  },
}

function appReducer(state, action) {
  switch (action.type) {
    // États généraux
    case "SET_LOADING":
      return { ...state, loading: action.payload }

    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false }

    case "SET_SUCCESS":
      return { ...state, success: action.payload, loading: false }

    case "CLEAR_ERROR":
      return { ...state, error: null }

    case "CLEAR_SUCCESS":
      return { ...state, success: null }

    case "LOAD_INITIAL_DATA":
      return { ...state, ...action.payload, loading: false, error: null }

    // Authentification
    case "SET_AUTH":
      return { ...state, user: action.payload.user, token: action.payload.token, loading: false }
    case "CLEAR_AUTH":
      return { ...state, user: null, token: null, loading: false }

    // Projets
    case "SET_PROJETS":
      return {
        ...state,
        projets: action.payload,
        loading: false,
        success: "Projets chargés avec succès !"
      }

    // Classeurs
    case "SET_CLASSEURS":
      return {
        ...state,
        classeurs: action.payload,
        loading: false,
        success: "Classeurs chargés avec succès !"
      }
    case "ADD_CLASSEUR":
      return {
        ...state,
        classeurs: [...state.classeurs, action.payload],
        loading: false,
        success: "Classeur créé avec succès !",
      }
    case "UPDATE_CLASSEUR":
      return {
        ...state,
        classeurs: state.classeurs.map((c) => (c.code_classeur === action.payload.code_classeur ? action.payload : c)),
        loading: false,
        success: "Classeur modifié avec succès !",
      }
    case "DELETE_CLASSEUR":
      return {
        ...state,
        classeurs: state.classeurs.filter((c) => c.code_classeur !== action.payload),
        loading: false,
        success: "Classeur supprimé avec succès !",
      }

    // Feuilles
    case "SET_FEUILLES":
      return {
        ...state,
        feuilles: action.payload,
        loading: false,
        success: "Feuilles chargées avec succès !"
      }
    case "ADD_FEUILLE":
      return {
        ...state,
        feuilles: [...state.feuilles, action.payload],
        loading: false,
        success: "Feuille créée avec succès !",
      }
    case "UPDATE_FEUILLE":
      return {
        ...state,
        feuilles: state.feuilles.map((f) => (f.code_feuille === action.payload.code_feuille ? action.payload : f)),
        loading: false,
        success: "Feuille modifiée avec succès !",
      }
    case "DELETE_FEUILLE":
      return {
        ...state,
        feuilles: state.feuilles.filter((f) => f.code_feuille !== action.payload),
        loading: false,
        success: "Feuille supprimée avec succès !",
      }

    // Types de champs
    case "SET_CHAMP_TYPES":
      return {
        ...state,
        champTypes: action.payload,
        loading: false,
        success: "Types de champs chargés avec succès !"
      }
    case "ADD_CHAMP_TYPE":
      return {
        ...state,
        champTypes: [...state.champTypes, action.payload],
        loading: false,
        success: "Type de champ créé avec succès !"
      }

    // Lignes
    case "SET_LIGNES":
      return {
        ...state,
        lignes: action.payload,
        loading: false,
        success: "Lignes chargées avec succès !"
      }
    case "ADD_LIGNES":
      return {
        ...state,
        lignes: [...state.lignes, ...action.payload],
        loading: false,
        success: "Ligne ajoutée avec succès !"
      }
    case "UPDATE_LIGNES":
      return {
        ...state,
        lignes: state.lignes.map((l) => (l.code_feuille_ligne === action.payload.code_feuille_ligne ? action.payload : l)),
        loading: false,
        success: "Ligne modifiée avec succès !",
      }
    case "DROP_LIGNES":
      return {
        ...state,
        lignes: state.lignes.filter((l) => !action.payload.includes(l.code_feuille_ligne)),
        loading: false,
        success: "Ligne supprimée avec succès !",
      }

    // Données de feuilles
    case "SET_FEUILLE_DATA":
      return {
        ...state,
        feuilleData: { ...state.feuilleData, [action.feuilleId]: action.payload },
        loading: false,
        success: "Données de feuille chargées avec succès !"
      }
    case "ADD_FEUILLE_DATA":
      return {
        ...state,
        feuilleData: {
          ...state.feuilleData,
          [action.feuilleId]: [...(state.feuilleData[action.feuilleId] || []), action.payload],
        },
        loading: false,
        success: "Ligne de données ajoutée avec succès !",
      }
    case "UPDATE_FEUILLE_DATA":
      return {
        ...state,
        feuilleData: {
          ...state.feuilleData,
          [action.feuilleId]: (state.feuilleData[action.feuilleId] || []).map((d) =>
            d.id === action.payload.id ? action.payload : d,
          ),
        },
        loading: false,
        success: "Ligne de données modifiée avec succès !",
      }
    case "DELETE_FEUILLE_DATA":
      return {
        ...state,
        feuilleData: {
          ...state.feuilleData,
          [action.feuilleId]: (state.feuilleData[action.feuilleId] || []).filter((d) => d.id !== action.payload),
        },
        loading: false,
        success: "Ligne de données supprimée avec succès !",
      }
    case "VALIDATE_FEUILLE_DATA":
      return {
        ...state,
        feuilleData: {
          ...state.feuilleData,
          [action.feuilleId]: (state.feuilleData[action.feuilleId] || []).map((d) =>
            d.id === action.payload.id ? action.payload : d,
          ),
        },
        loading: false,
        success: "Ligne de données validée avec succès !",
      }

    // Cache
    case "UPDATE_CACHE":
      return {
        ...state,
        cache: {
          ...state.cache,
          lastFetch: { ...state.cache.lastFetch, [action.key]: Date.now() },
          data: { ...state.cache.data, [action.key]: action.data },
        },
        loading: false,
      }

    default:
      return state
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState)

  // Auto-clear success messages
  useEffect(() => {
    loadInitialData()

    if (state.success) {
      const timer = setTimeout(() => {
        dispatch({ type: "CLEAR_SUCCESS" })
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [state.success])

  // Sauvegarde automatique des données locales
  useEffect(() => {
    const localData = {
      user: state.user,
      token: state.token,
    }
    localStorage.setItem("data-local", JSON.stringify(localData))
  }, [state.user, state.token])

  // Sauvegarde de backup complète
  useEffect(() => {
    const backupData = {
      projets: state.projets,
      classeurs: state.classeurs,
      feuilles: state.feuilles,
      lignes: state.lignes,
      champTypes: state.champTypes,
      user: state.user,
      token: state.token,
    }
    localStorage.setItem("data-backup", JSON.stringify(backupData))
  }, [state])

  // ==========================================
  // FONCTIONS DE CHARGEMENT INITIAL
  // ==========================================
  const loadInitialData = async () => {
    try {
      // dispatch({ type: "SET_LOADING", payload: true })

      // Load auth from localStorage
      const localAuth = localStorage.getItem("data-local")
      let user = null
      let token = null
      if (localAuth) {
        const parsedLocalAuth = JSON.parse(localAuth)
        user = parsedLocalAuth.user
        token = parsedLocalAuth.token
        if (token) {
          // Set token for axios
          // api.defaults.headers.common['Authorization'] = `Bearer ${token}`; // Handled by interceptor
        }
      }

      // Charger les données depuis les APIs
      const [projetsData, classeursData, feuillesData, lignesData, champTypesData] = await Promise.allSettled([
        getProjets(),
        getClasseurs(),
        getFeuilles(),
        getFeuilleLignes(),
        getChampTypes(),
      ])

      // Dispatcher les données
      dispatch({
        type: "LOAD_INITIAL_DATA",
        payload: {
          projets: projetsData.status === "fulfilled" ? projetsData.value.data || projetsData.value : [],
          classeurs: classeursData.status === "fulfilled" ? classeursData.value.data || classeursData.value : [],
          feuilles: feuillesData.status === "fulfilled" ? feuillesData.value.data || feuillesData.value : [],
          lignes: lignesData.status === "fulfilled" ? lignesData.value.data || lignesData.value : [],
          champTypes: champTypesData.status === "fulfilled" ? champTypesData.value.data || champTypesData.value : [],
          user,
          token,
        },
      })
    } catch (error) {
      console.error("Erreur lors du chargement initial:", error)

      const backupData = localStorage.getItem("data-backup")
      if (backupData) {
        dispatch({ type: "LOAD_INITIAL_DATA", payload: JSON.parse(backupData) })
      } else {
        dispatch({ type: "SET_ERROR", payload: "Impossible de charger les données" })
      }
    }
  }

  // ==========================================
  // ACTIONS POUR L'AUTHENTIFICATION - NEW
  // ==========================================
  const login = (user, token) => {
    localStorage.setItem("app-token", token)
    dispatch({ type: "SET_AUTH", payload: { user, token } })
  }

  const logout = () => {
    localStorage.removeItem("app-token")
    dispatch({ type: "CLEAR_AUTH" })
  }

  // ==========================================
  // ACTIONS POUR LES PROJETS
  // ==========================================
  const getProjetAPI = async (projectId) => {
    try {
      const resProjet = await getProjetById(projectId)
      const projet = resProjet.data || resProjet
      dispatch({ type: "LOAD_INITIAL_DATA", payload: projet })
      return projet
    } catch (error) {
      console.error("Erreur lors du chargement du projet:", error)
      dispatch({ type: "LOAD_INITIAL_DATA", payload: { error: "Projet non trouvé" } })
      throw error
    }
  }

  // ==========================================
  // ACTIONS POUR LES CLASSEURS
  // ==========================================
  const getClasseurAPI = async (id) => {
    try {
      const response = await getClasseurID(id)
      dispatch({ type: "LOAD_INITIAL_DATA", payload: response.data || response })
      return response.data || response
    } catch (error) {
      console.error("Erreur lors du chargement du classeur:", error)
      dispatch({ type: "LOAD_INITIAL_DATA", payload: { error: "Classeur non trouvé" } })
      throw error
    }
  }

  const addClasseurAPI = async (classeurData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await createClasseur(classeurData)
      const newClasseur = response.data || response
      dispatch({ type: "ADD_CLASSEUR", payload: newClasseur })
      return newClasseur
    } catch (error) {
      console.error("Erreur lors de la création du classeur:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la création du classeur" })
      throw error
    }
  }

  const updateClasseurAPI = async (id, classeurData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await updateClasseur(id, classeurData)
      const updatedClasseur = response.data || response
      dispatch({ type: "UPDATE_CLASSEUR", payload: updatedClasseur })
      return updatedClasseur
    } catch (error) {
      console.error("Erreur lors de la modification du classeur:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la modification du classeur" })
      throw error
    }
  }

  const deleteClasseurAPI = async (id) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      await deleteClasseur(id)
      dispatch({ type: "DELETE_CLASSEUR", payload: id })
    } catch (error) {
      console.error("Erreur lors de la suppression du classeur:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la suppression du classeur" })
      throw error
    }
  }

  // ==========================================
  // ACTIONS POUR LES FEUILLES
  // ==========================================
  const addFeuilleAPI = async (feuilleData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await createFeuille(feuilleData)
      const newFeuille = response.data || response
      dispatch({ type: "ADD_FEUILLE", payload: newFeuille })
      return newFeuille
    } catch (error) {
      console.error("Erreur lors de la création de la feuille:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la création de la feuille" })
      throw error
    }
  }

  const updateFeuilleAPI = async (id, feuilleData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await updateFeuille(id, feuilleData)
      const updatedFeuille = response.data || response
      dispatch({ type: "UPDATE_FEUILLE", payload: updatedFeuille })
      return updatedFeuille
    } catch (error) {
      console.error("Erreur lors de la modification de la feuille:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la modification de la feuille" })
      throw error
    }
  }

  const deleteFeuilleAPI = async (id) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      await deleteFeuille(id)
      dispatch({ type: "DELETE_FEUILLE", payload: id })
    } catch (error) {
      console.error("Erreur lors de la suppression de la feuille:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la suppression de la feuille" })
      throw error
    }
  }

  const getFeuilleAPI = async (id) => {
    try {
      const response = await getFeuilleID(id)
      dispatch({ type: "LOAD_INITIAL_DATA", payload: response.data || response })
      return response.data || response
    } catch (error) {
      console.error("Erreur lors du chargement de la feuille:", error)
      dispatch({ type: "LOAD_INITIAL_DATA", payload: { error: "Feuille non trouvée" } })
      throw error
    }
  }


  // ==========================================
  // ACTIONS POUR LES TYPES DE CHAMPS
  // ==========================================
  const addChampTypeAPI = async (champTypeData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await createChampType(champTypeData)
      const newChampType = response.data || response
      dispatch({ type: "ADD_CHAMP_TYPE", payload: newChampType })
      return newChampType
    } catch (error) {
      console.error("Erreur lors de la création du type de champ:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la création du type de champ" })
      throw error
    }
  }

  // ==========================================
  // ACTIONS POUR LES LIGNES
  // ==========================================
  const addLignesAPI = async (lignesData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await addFeuilleLigne(lignesData)
      const newLignes = response.data || response
      dispatch({ type: "ADD_LIGNES", payload: Array.isArray(newLignes) ? newLignes : [newLignes] })
      return newLignes
    } catch (error) {
      console.error("Erreur lors de la création des lignes:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la création des lignes" })
      throw error
    }
  }

  const updateLigneAPI = async (lignesData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await updateFeuilleLigne(lignesData)
      const newLignes = response.data || response
      dispatch({ type: "UPDATE_LIGNES", payload: Array.isArray(newLignes) ? newLignes : [newLignes] })
      return newLignes
    } catch (error) {
      console.error("Erreur lors de la mise à jour des lignes:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la mise à jour des lignes" })
      throw error
    }
  }

  const deleteLignesAPI = async (lignesData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await deleteFeuilleLigneColumns(lignesData)
      const newLignes = response.data || response
      dispatch({ type: "DROP_LIGNES", payload: Array.isArray(newLignes) ? newLignes : [newLignes] })
      return newLignes
    } catch (error) {
      console.error("Erreur lors de la suppression des lignes:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la suppression des lignes" })
      throw error
    }
  }

  const updateLigneRangAPI = async (data) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await updateFeuilleLigneRang(data)
      dispatch({ type: "SET_LOADING", payload: false })
      return response.data || response
    } catch (error) {
      console.error("Erreur lors de la mise à jour du rang:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la mise à jour du rang" })
      throw error
    }
  }

  // ==========================================
  // ACTIONS POUR LES DONNÉES DE FEUILLES
  // ==========================================
  const getFeuilleDataAPI = async (feuilleId) => {
    try {
      const response = await getFeuilleData(feuilleId)
      const data = response.data || response
      dispatch({ type: "LOAD_INITIAL_DATA", payload: { feuilleData: data } })
      return data
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error)
      dispatch({ type: "LOAD_INITIAL_DATA", payload: { error: "Erreur lors du chargement des données" } })
      throw error
    }
  }

  const addFeuilleDataAPI = async (dataEntry) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await createFeuilleData(dataEntry)
      const newDataEntry = response.data || response
      dispatch({ type: "ADD_FEUILLE_DATA", payload: newDataEntry })
      return newDataEntry
    } catch (error) {
      console.error("Erreur lors de l'ajout des données:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de l'ajout des données" })
      throw error
    }
  }

  const updateFeuilleDataAPI = async (dataEntryId, dataEntry) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await updateFeuilleData(dataEntryId, dataEntry)
      const updatedDataEntry = response.data || response
      dispatch({ type: "UPDATE_FEUILLE_DATA", payload: updatedDataEntry })
      return updatedDataEntry
    } catch (error) {
      console.error("Erreur lors de la modification des données:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la modification des données" })
      throw error
    }
  }

  const deleteFeuilleDataAPI = async (dataEntryId) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await deleteFeuilleData(dataEntryId)
      const deletedDataEntry = response.data || response
      dispatch({ type: "DELETE_FEUILLE_DATA", payload: deletedDataEntry })
      return deletedDataEntry
    } catch (error) {
      console.error("Erreur lors de la suppression des données:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la suppression des données" })
      throw error
    }
  }

  const validateFeuilleDataAPI = async (dataEntryId) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true })
      const response = await validateFeuilleData(dataEntryId)
      const validatedDataEntry = response.data || response
      dispatch({ type: "VALIDATE_FEUILLE_DATA", payload: validatedDataEntry })
      return validatedDataEntry
    } catch (error) {
      console.error("Erreur lors de la validation des données:", error)
      dispatch({ type: "SET_ERROR", payload: "Erreur lors de la validation des données" })
      throw error
    }
  }

  // ==========================================
  // UTILITAIRES
  // ==========================================

  const setSuccess = (message) => {
    dispatch({ type: "SET_SUCCESS", payload: message })
  }

  const clearError = () => {
    dispatch({ type: "CLEAR_ERROR" })
  }

  const clearSuccess = () => {
    dispatch({ type: "CLEAR_SUCCESS" })
  }

  const getProjetStats = (projetId) => {
    const projectClasseurs = state.classeurs.filter((c) => c.id_projet === Number.parseInt(projetId))
    const totalFeuilles = projectClasseurs.reduce((acc, classeur) => {
      return acc + state.feuilles.filter((f) => f.code_classeur === classeur.code_classeur).length
    }, 0)
    return { classeurs: projectClasseurs.length, feuilles: totalFeuilles }
  }

  const getAllProjects = () => {
    return state.projets || []
  }

  // ==========================================
  // VALEUR DU CONTEXTE
  // ==========================================
  const value = {
    // État
    state,
    dispatch,
    loading: state.loading,
    error: state.error,
    success: state.success,
    user: state.user,
    token: state.token,

    // Actions Authentification - NEW
    login,
    logout,

    // Actions API - Projets
    getProjetAPI,

    // Actions API - Classeurs
    getClasseurAPI,
    addClasseurAPI,
    updateClasseurAPI,
    deleteClasseurAPI,

    // Actions API - Feuilles
    getFeuilleAPI,
    addFeuilleAPI,
    updateFeuilleAPI,
    deleteFeuilleAPI,

    // Actions API - Types de champs
    addChampTypeAPI,

    // Actions API - Lignes
    addLignesAPI,
    updateLigneAPI,
    updateLigneRangAPI,
    deleteLignesAPI,

    // Actions API - Données de feuilles
    getFeuilleDataAPI,
    addFeuilleDataAPI,
    updateFeuilleDataAPI,
    deleteFeuilleDataAPI,
    validateFeuilleDataAPI,

    // Utilitaires
    setSuccess,
    clearError,
    clearSuccess,
    getProjetStats,
    getAllProjects,

    getProjet: (id) => state.projets.find((p) => p.id === Number.parseInt(id)),
    getClasseur: (id) => state.classeurs.find((c) => c.code_classeur === Number.parseInt(id)),
    getClasseursByProject: (projectId) => state.classeurs.filter((c) => c.id_projet === Number.parseInt(projectId)),
    getFeuille: (id) => state.feuilles.find((f) => f.code_feuille === Number.parseInt(id)),
    getFeuillesByClasseur: (classeurId) => state.feuilles.filter((f) => f.code_classeur === Number.parseInt(classeurId)),
    getFeuilleLignes: (feuilleId) => state.lignes.filter((l) => l.code_feuille === Number.parseInt(feuilleId)),
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useApp() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error("useApp must be used within an AppProvider")
  }
  return context
}
